import {
  AvailableOptions,
  CartResponse,
  CartResponseFormatted,
  ICartItem,
  PromotionProduct,
} from '@/types/cart'
import { WishlistResponse, WishlistResponseFormatted } from '@/types/wishlist'

type OptionsType = {
  option1: string
  option2: string
  option3: string
  option4: string
  [key: string]: string
}

const reFormatCartResponse = (data: CartResponse): CartResponseFormatted => {
  if (!data || !data.items) {
    return {
      ...data,
      items: [],
      original: data,
      promotionProducts: [],
    } as CartResponseFormatted
  }

  const formattedItems = formatAvailableOptions(data.items)

  const { formattedItems: formattedItemsWithNestedGift, nestedGiftIndexIds } =
    formatNestedGift(formattedItems)

  // Remove only gift items that have been nested into a parent
  // Keep orphan gift items (those without a matching parent) to display as standalone items
  const filteredItems = formattedItemsWithNestedGift.filter((item) => {
    // Keep items without index_parent_id (main products)
    if (!item.index_parent_id) return true

    // Keep gift items that haven't been nested (orphan gifts)
    // These will be displayed as standalone items
    if (item.index_id && !nestedGiftIndexIds.has(item.index_id)) {
      return true
    }

    // Remove gift items that have been nested into a parent
    return false
  })

  // Sắp xếp items để các item có paired_with_index_id đứng ngay dưới item có index_id tương ứng
  // Chỉ áp dụng khi campaign isBuy1Get1ValueMatchCampaignActive đang active (có items có paired_with_index_id)
  const items = sortCartItemsByPairedIndex(filteredItems)

  const promotionProducts = formatPromotionProducts(data.promotion_products)

  return {
    ...data,
    items,
    promotionProducts,
    original: data,
  } as CartResponseFormatted
}

const reFormatWishlistResponse = (
  data: WishlistResponse
): WishlistResponseFormatted => {
  if (!data || !data.data) {
    return {
      ...data,
      data: [],
    } as WishlistResponseFormatted
  }

  const formattedItems = formatAvailableOptions(data.data)

  return {
    ...data,
    data: formattedItems,
  } as WishlistResponseFormatted
}

const formatAvailableOptions = <T extends ICartItem>(
  items: T[]
): (T & { availableOptions: AvailableOptions; is_sold_out?: boolean })[] => {
  if (!items || items.length === 0)
    return [] as (T & {
      availableOptions: AvailableOptions
      is_sold_out?: boolean
    })[]

  const formattedItems = items.map((item) => {
    const options: OptionsType = {
      option1: '',
      option2: '',
      option3: '',
      option4: '',
    }

    const availableOptions =
      item.options?.map((option, index) => {
        const optionKey = `option${index + 1}` as keyof OptionsType
        const existVariant =
          item.variants?.[optionKey as keyof typeof item.variants]
        const variantValue =
          typeof existVariant === 'string' ? existVariant : ''

        if (variantValue) {
          options[optionKey] = variantValue
        } else {
          delete options[optionKey]
        }

        return {
          index,
          key: optionKey,
          option: option.values,
          value: variantValue,
        }
      }) || []

    return {
      ...item,
      options,
      availableOptions,
    } as T & { availableOptions: AvailableOptions }
  })

  // After formatting, filter out hidden variants and mark items as sold out
  return formattedItems.map(filterItemVariants)
}

const filterItemVariants = <T extends ICartItem>(
  item: T & { availableOptions: AvailableOptions }
) => {
  const variantTitle = item.variants?.title
  const is_sold_out = item.variants_hide?.includes(variantTitle) || false

  const availableOptions = item.availableOptions.map((option) => {
    const optionKey = option.key as keyof OptionsType
    const currentValue = option.value
    const filteredValues = option.option.filter((value) => {
      // Keep the currently selected value
      if (value === currentValue) return true

      // Handle color options
      if (optionKey === 'option1' && item.variants?.option2) {
        const combination = `${value} / ${item.variants.option2}`
        return (
          !item.variants_hide?.includes(combination) || value === currentValue
        )
      }

      // Handle size options
      if (optionKey === 'option2' && item.variants?.option1) {
        const combination = `${item.variants.option1} / ${value}`
        return (
          !item.variants_hide?.includes(combination) || value === currentValue
        )
      }

      return true
    })

    return { ...option, option: filteredValues }
  })

  return { ...item, is_sold_out, availableOptions }
}

const formatNestedGift = (itemsWithAvailableOptions: ICartItem[]) => {
  const giftItems = itemsWithAvailableOptions.filter(
    (item) => item?.index_parent_id
  )

  // Track which gift items have been nested into a parent
  const nestedGiftIndexIds = new Set<string>()

  const formattedItems = itemsWithAvailableOptions.map((item) => {
    // Xử lý nestedGift vào trong item
    if (giftItems.length > 0) {
      const indexId = item.index_id ?? `${item.id}-${item.variants?._id}`
      const matchedGifts = giftItems.filter(
        (giftItem) => giftItem.index_parent_id === indexId
      )

      // Track gift items that have been nested
      matchedGifts.forEach((gift) => {
        if (gift.index_id) {
          nestedGiftIndexIds.add(gift.index_id)
        }
      })

      item.nestedGift = matchedGifts
    }

    return item
  })

  return { formattedItems, nestedGiftIndexIds }
}

const formatPromotionProducts = (promotionProducts: PromotionProduct[]) => {
  if (!promotionProducts || promotionProducts.length === 0) return []

  return promotionProducts.map((product) => {
    return {
      ...product,
    }
  })
}

/**
 * Sắp xếp cart items để các item có paired_with_index_id đứng ngay dưới item có index_id tương ứng
 * Chỉ áp dụng khi có items có paired_with_index_id (tức là campaign isBuy1Get1ValueMatchCampaignActive đang active)
 *
 * @param items - Array cart items cần sắp xếp
 * @returns Array items đã được sắp xếp
 */
const sortCartItemsByPairedIndex = (items: ICartItem[]): ICartItem[] => {
  if (!items || items.length === 0) return items

  // Kiểm tra xem có items nào có paired_with_index_id không
  // Nếu không có, return items nguyên bản (campaign không active)
  const hasPairedItems = items.some((item) => item.paired_with_index_id)
  if (!hasPairedItems) return items

  // Tách items thành 2 nhóm: items có paired_with_index_id và items không có
  const pairedItems: ICartItem[] = []
  const regularItems: ICartItem[] = []

  items.forEach((item) => {
    if (item.paired_with_index_id) {
      pairedItems.push(item)
    } else {
      regularItems.push(item)
    }
  })

  // Nếu không có paired items, return items nguyên bản
  if (pairedItems.length === 0) return items

  // Tạo array kết quả
  const sortedItems: ICartItem[] = []
  const processedPairedIndexIds = new Set<string>()

  // Duyệt qua regular items và chèn paired items ngay sau parent
  regularItems.forEach((item) => {
    sortedItems.push(item)

    // Tìm các paired items có paired_with_index_id === item.index_id
    const matchedPairedItems = pairedItems.filter(
      (paired) =>
        paired.paired_with_index_id === item.index_id &&
        paired.index_id &&
        !processedPairedIndexIds.has(paired.index_id)
    )

    // Chèn các paired items ngay sau parent item
    matchedPairedItems.forEach((paired) => {
      sortedItems.push(paired)
      if (paired.index_id) {
        processedPairedIndexIds.add(paired.index_id)
      }
    })
  })

  // Thêm các orphaned paired items (không tìm thấy parent) vào cuối
  pairedItems.forEach((paired) => {
    if (paired.index_id && !processedPairedIndexIds.has(paired.index_id)) {
      sortedItems.push(paired)
      processedPairedIndexIds.add(paired.index_id)
    }
  })

  return sortedItems
}
export const CartFormatter = {
  reFormatCartResponse,
  reFormatWishlistResponse,
}
